import React, { useState } from 'react';
import { Modal, DatePicker, Button, Input, Image } from 'antd'; 
const { RangePicker } = DatePicker;

const HistoryModal = ({
  isHistoryModalOpen,
  setIsHistoryModalOpen,
  historyDateRange,
  setHistoryDateRange,
  filterHistoryByDate,
  historyData,
  onEditComment,
  userRole,
}) => {
  const [editRecordId, setEditRecordId] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const shouldShowEditButton = (record) => {
    // Если это action ИЛИ edit c action="manual_status",
    // то показываем кнопку редактирования для champ & main_engineer
    if (
      record.changeType === 'action' ||
      (record.changeType === 'edit' && record.action === 'manual_status')
    ) {
      return ['champ', 'main_engineer'].includes(userRole);
    }
    return false;
  };

  const handleEditClick = (historyItem) => {
    setEditRecordId(historyItem.id);

    let currentComment = historyItem.comment || '';
    if (!currentComment && Array.isArray(historyItem.changes)) {
      const commentChange = historyItem.changes.find((c) => c.field === 'comment');
      if (commentChange && commentChange.new) {
        currentComment = commentChange.new;
      }
    }

    setNewComment(currentComment);
    setIsEditModalOpen(true);
  };

  const handleCancelEdit = () => {
    setEditRecordId(null);
    setNewComment('');
    setIsEditModalOpen(false);
  };

  const handleSubmitEdit = () => {
    if (!newComment.trim()) {
      alert('Введите новый комментарий!');
      return;
    }
    onEditComment(editRecordId, newComment);
    setEditRecordId(null);
    setNewComment('');
    setIsEditModalOpen(false);
  };

  const displayedHistory = filterHistoryByDate();

  // Группируем записи (как и раньше)
  const grouped = {};
  displayedHistory.forEach((rec) => {
    if (!rec.parentId) {
      if (!grouped[rec.id]) {
        grouped[rec.id] = { root: rec, edits: [] };
      } else {
        grouped[rec.id].root = rec;
      }
    } else {
      if (!grouped[rec.parentId]) {
        grouped[rec.parentId] = { root: null, edits: [] };
      }
      grouped[rec.parentId].edits.push(rec);
    }
  });

  const groupedArr = Object.values(grouped);

  return (
    <>
      <Modal
        title="История изменений"
        open={isHistoryModalOpen}
        onOk={() => setIsHistoryModalOpen(false)}
        onCancel={() => setIsHistoryModalOpen(false)}
        width={800}
      >
        <RangePicker
          value={historyDateRange}
          onChange={(dates) => setHistoryDateRange(dates || [])}
          style={{ marginBottom: 16, display: 'block' }}
          allowClear
        />

        {groupedArr.length > 0 ? (
          groupedArr.map((block, index) => {
            const rootItem = block.root;
            const edits = block.edits;

            return (
              <div
                key={index}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                {rootItem && (
                  <RenderHistoryItem
                    historyItem={rootItem}
                    onEditClick={handleEditClick}
                    shouldShowEditButton={shouldShowEditButton}
                  />
                )}

                {edits.map((editItem, i) => (
                  <div
                    key={i}
                    style={{
                      marginTop: 10,
                      marginLeft: 20,
                      paddingLeft: 10,
                      borderLeft: '2px solid #d9d9d9',
                    }}
                  >
                    <RenderHistoryItem
                      historyItem={editItem}
                      onEditClick={handleEditClick}
                      shouldShowEditButton={() => false}
                    />
                  </div>
                ))}
              </div>
            );
          })
        ) : (
          <p>История изменений отсутствует или не попадает в выбранный диапазон.</p>
        )}
      </Modal>

      <Modal
        title="Редактировать комментарий"
        open={isEditModalOpen}
        onCancel={handleCancelEdit}
        footer={[
          <Button key="cancel" onClick={handleCancelEdit}>
            Отмена
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmitEdit}>
            Сохранить
          </Button>,
        ]}
      >
        <Input.TextArea
          rows={3}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
        />
      </Modal>
    </>
  );
};

// Компонент для отображения одного блока истории
const RenderHistoryItem = ({ historyItem, onEditClick, shouldShowEditButton }) => {
  // Попробуем всегда показывать "Автор изменения"
  // (на бэке возвращаем editedByName)
  const authorName = historyItem.editedByName || 'Неизвестно';

  // Если это "comment_edit"
  if (historyItem.changeType === 'comment_edit') {
    let parsedNewValue;
    try {
      parsedNewValue = JSON.parse(historyItem.newValue || '{}');
    } catch (e) {
      parsedNewValue = {};
    }

    const newComment = parsedNewValue.comment || '(пусто)';

    return (
      <div>
        <p><strong>Дата редактирования:</strong> {historyItem.timestamp}</p>
        <p><strong>Автор изменения:</strong> {authorName}</p>
        <p><strong>Отредактированный комментарий:</strong> {newComment}</p>
      </div>
    );
  }

  // Иначе (action/manual_status/...)
  const isActionOrManualStatus =
    historyItem.changeType === 'action' ||
    (historyItem.changeType === 'edit' && historyItem.action === 'manual_status');

  return (
    <div>
      <p><strong>Дата изменения:</strong> {historyItem.timestamp}</p>
      <p><strong>Автор изменения:</strong> {authorName}</p>

      {isActionOrManualStatus && (
        <>
          <p><strong>Действие:</strong> {historyItem.action}</p>
          <p><strong>Комментарий:</strong> {historyItem.comment || 'Без комментария'}</p>
        </>
      )}

      <p><strong>Изменено:</strong></p>
      <ul>
        {historyItem.changes.map((change, idx) => (
          <li key={idx}>
            <strong>{change.field}</strong>: {change.old || 'пусто'} → {change.new || 'пусто'}
          </li>
        ))}
      </ul>

      {/* Фотографии, если есть */}
      <RenderPhotoPreview photos={historyItem.photos || []} />

      {shouldShowEditButton(historyItem) && (
        <Button onClick={() => onEditClick(historyItem)} style={{ marginTop: '8px' }}>
          Редактировать
        </Button>
      )}
    </div>
  );
};

// ---------------------------------------
// Компонент для фото: АнтД Image + preview
// ---------------------------------------

function RenderPhotoPreview({ photos }) {
  const [showAll, setShowAll] = useState(false);

  if (!photos || photos.length === 0) {
    return null;
  }

  // показываем либо все, либо первые 2
  const displayed = showAll ? photos : photos.slice(0, 2);

  return (
    <div style={{ marginTop: 8 }}>
      <p><strong>Прикреплённые фото:</strong></p>
      <Image.PreviewGroup>
        {displayed.map((photo) => (
          <Image
            key={photo.id}
            width={120}
            src={photo.file_path}
            alt="Фото"
            style={{ margin: 4, border: '1px solid #ccc', borderRadius: 4 }}
          />
        ))}
      </Image.PreviewGroup>

      {photos.length > 2 && !showAll && (
        <Button size="small" onClick={() => setShowAll(true)}>
          Показать все ({photos.length})
        </Button>
      )}
    </div>
  );
}

export default HistoryModal;
